import * as THREE from 'three';

/**
 * The viewer in the world while presenting: what `Stage.activeCamera` is in a headset, for anything
 * that reads the head's pose -- its position, its turn, its matrixWorld -- in a frame handler or an
 * input event.
 *
 * Three's XR camera (`renderer.xr.getCamera()`) has no parent. Three (r186, WebXRManager's
 * onAnimationFrame) sets it to the left eye's pose in the reference space at the start of every XR
 * frame and puts it in the world only at the render, so read in a frame handler it is where the
 * pupil stood when the session began, wherever a VR lesson moved and turned the rig (a step's
 * view, a villa room). So in a frame handler this is the rig's pose times the XR camera's, this frame's; between
 * frames, and in a frame without a viewer pose (three leaves the camera alone then), it is where
 * three rendered from. With the rig where AR leaves it -- at the origin, unturned -- that is exactly
 * what the XR camera gave.
 */
export class XrViewer {
  /** Never rendered: a camera so that getWorldDirection looks along −z and the projection comes along. */
  readonly camera = new THREE.PerspectiveCamera();
  readonly #local = new THREE.Matrix4();
  #inFrame = false;

  /** At the start of each XR frame, before anything reads the viewer: whether it has a viewer pose. */
  frameStarted(posed: boolean): void {
    this.#inFrame = posed;
  }

  /** After the render: three's XR camera is in the world now. */
  rendered(): void {
    this.#inFrame = false;
  }

  /** The viewer now, from three's XR camera and the rig. Valid until the next read. */
  read(xr: THREE.Camera, rig: THREE.Object3D): THREE.PerspectiveCamera {
    const camera = this.camera;
    if (this.#inFrame) {
      rig.updateWorldMatrix(true, false);
      this.#local.compose(xr.position, xr.quaternion, xr.scale);
      camera.matrixWorld.multiplyMatrices(rig.matrixWorld, this.#local);
    } else {
      camera.matrixWorld.copy(xr.matrixWorld);
    }
    camera.matrixWorld.decompose(camera.position, camera.quaternion, camera.scale);
    camera.matrix.copy(camera.matrixWorld);
    camera.matrixWorldInverse.copy(camera.matrixWorld).invert();
    camera.projectionMatrix.copy(xr.projectionMatrix);
    camera.projectionMatrixInverse.copy(xr.projectionMatrixInverse);
    return camera;
  }
}
